import React, { createContext, useState, useEffect } from "react";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [username, setUsername] = useState(null);
  const [loading, setLoading] = useState(true);       // App-level loading
  const [authLoading, setAuthLoading] = useState(false); // Login/Register/OTP actions
  const [tokenExpiry, setTokenExpiry] = useState(null);

  // Load token + user info from localStorage on first render
  useEffect(() => {
    const loadStoredData = () => {
      setLoading(true);
      const storedToken = localStorage.getItem("token");
      const storedUser = localStorage.getItem("username");
      const storedExpiry = localStorage.getItem("token_expiry");

      if (storedToken && storedUser && storedExpiry) {
        const now = Math.floor(Date.now() / 1000);
        if (now < parseInt(storedExpiry, 10)) {
          setToken(storedToken);
          setUsername(storedUser);
          setTokenExpiry(parseInt(storedExpiry, 10));
        } else {
          logout();
        }
      }
      setLoading(false);
    };

    loadStoredData();
  }, []);

  // Helper: Save token + user + expiry in localStorage
  const setTokenData = (newToken, user, expiry) => {
    localStorage.setItem("token", newToken);
    localStorage.setItem("username", user);
    localStorage.setItem("token_expiry", expiry.toString());
    setToken(newToken);
    setUsername(user);
    setTokenExpiry(expiry);
  };

  // Logout user
  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    localStorage.removeItem("token_expiry");
    setToken(null);
    setUsername(null);
    setTokenExpiry(null);
  };

  // Helper: fetch with Authorization header
  const authFetch = async (url, options = {}) => {
    if (!token) throw new Error("No auth token");
    const res = await fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });
      if (res.status === 401) {
    logout();
    throw new Error("Unauthorized");
  }
    return res.json();
  };

  // API methods
  const api = {
    getGallery: (path = "root") =>
      authFetch(`https://printmycake.com/cms/api/gallery.php?path=${path}`),

    saveSelection: (path, images) =>
      authFetch("https://printmycake.com/cms/api/save_selection.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, path, images }),
      }),

    uploadImages: (formData) =>
      fetch("https://printmycake.com/cms/api/upload_images.php", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      }).then((res) => res.json()),
  };

  // Validate token expiration
  const checkTokenValid = async () => {
    if (!tokenExpiry) return false;
    const now = Math.floor(Date.now() / 1000);
    if (now >= tokenExpiry) {
      logout();
      return false;
    }
    return true;
  };

  // Login: Step 1 - validate user and check if OTP required
  const login = async (username, password) => {
    setAuthLoading(true);
    try {
      const res = await fetch("https://printmycake.com/cms/api/login.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      setAuthLoading(false);

      if (res.ok && data.ok && data.otp_required && data.user_id) {
        return {
          otpRequired: true,
          userId: data.user_id,
          expiry: data.otp_expires_at,
        };
      } else {
        return {
          otpRequired: false,
          success: false,
          error: data.error || "Login failed",
        };
      }
    } catch (err) {
      console.error(err);
      setAuthLoading(false);
      return { otpRequired: false, success: false, error: "Network error" };
    }
  };

  // Login: Step 2 - verify OTP
  const verifyOtp = async (userId, otp) => {
    setAuthLoading(true);
    try {
      const res = await fetch("https://printmycake.com/cms/api/verify_otp.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, otp, purpose: "login" }),
      });
      const data = await res.json();
      setAuthLoading(false);

      if (res.ok && data.ok && data.token) {
        const expiry = data.expiry; // seconds timestamp
        await setTokenData(data.token, data.user.username, expiry);
        return true;
      }
      return false;
    } catch (err) {
      console.error(err);
      setAuthLoading(false);
      return false;
    }
  };

  // Resend OTP
  const resendOtp = async (userId) => {
    try {
      const res = await fetch("https://printmycake.com/cms/api/resend_otp.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, purpose: "login" }),
      });
      const data = await res.json();
      return res.ok && data.ok;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  // Request password reset OTP
  const requestPasswordOtp = async (email) => {
    try {
      const res = await fetch("https://printmycake.com/cms/api/request_password_reset.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      return res.ok && data.ok;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  // Reset password
  const resetPassword = async (email, otp, newPassword) => {
    try {
      const res = await fetch("https://printmycake.com/cms/api/reset_password.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp, new_password: newPassword }),
      });
      const data = await res.json();
      return res.ok && data.ok;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

    const getPendingUsers = async () => {
  const valid = await checkTokenValid();
  if (!valid) throw new Error("Session expired");

  return await authFetch(
    "https://printmycake.com/cms/api/get_pending_users.php"
  );
};

  const approveUser = async (userId, action) => {
    return await authFetch(
      "https://printmycake.com/cms/api/approve_user.php",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          action, // approve | deny
        }),
      }
    );
  };

  // 🔹 Get all users (admin)
const getAllUsers = async () => {
  return await authFetch(
    "https://printmycake.com/cms/api/get_users.php"
  );
};

// 🔹 Manage user status (activate / block / unblock / pending)
const manageUserStatus = async (userId, action) => {
  return await authFetch(
    "https://printmycake.com/cms/api/manage_user.php",
    {
      method: "POST",
      body: JSON.stringify({
        user_id: userId,
        action,
      }),
    }
  );
};

// GET USER ORDERS
const getOrders = async () => {
  const token = localStorage.getItem("token");

  const res = await fetch(
    "http://localhost/api/get_upload_history.php",
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return res.json();
};

// CREATE ORDER
const createOrder = async (amount) => {
  const token = localStorage.getItem("token");

  const res = await fetch(
    "http://localhost/api/create_order.php",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ amount }),
    }
  );

  return res.json();
};

// VERIFY RAZORPAY PAYMENT
const verifyPayment = async (payload) => {
  const token = localStorage.getItem("token");

  const res = await fetch(
    "http://localhost/api/verify_razorpay_payment.php",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }
  );

  return res.json();
};

const getAllOrders = async () => {
  try {
    const res = await fetch(
      "https://printmycake.com/cms/api/admin_get_orders.php",
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return await res.json();
  } catch (err) {
    console.error("Admin getOrders error:", err);
    return { ok: false };
  }
};

const updateOrderStatus = async (orderId, status) => {
  try {
    const res = await fetch(
      "https://printmycake.com/cms/api/admin_update_order_status.php",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          order_id: orderId,
          status,
        }),
      }
    );
    return await res.json();
  } catch (err) {
    console.error("Admin updateOrderStatus error:", err);
    return { ok: false };
  }
};

const getOrderPdf = async (orderId) => {
  try {
    const res = await fetch(
      `https://printmycake.com/cms/api/admin_get_order_files.php?order_id=${orderId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return await res.json();
  } catch (err) {
    console.error("Admin getOrderPdf error:", err);
    return { ok: false };
  }
};

const deleteOrder = async (orderId) => {
  try {
    const res = await fetch(
      "https://printmycake.com/cms/api/admin_delete_order.php",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ order_id: orderId }),
      }
    );
    return await res.json();
  } catch (err) {
    console.error("Admin deleteOrder error:", err);
    return { ok: false };
  }
};

  return (
    <AuthContext.Provider
      value={{
        token,
        username,
        loading,
        authLoading,
        checkTokenValid,
        authFetch,
        login,
        verifyOtp,
        resendOtp,
        requestPasswordOtp,
        resetPassword,
        api,
        logout,
        getPendingUsers,
        approveUser,
        manageUserStatus,
        getAllUsers,
        getOrders,
        createOrder,
        verifyPayment,
        getAllOrders,
        updateOrderStatus,
        getOrderPdf,
        deleteOrder,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
